import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaBolt, FaThList, FaThLarge } from "react-icons/fa";

export const ATTRACTIONS = [
  {
    id: "standard-visit",
    title: "(Combo) Science Centre + Energy Capsule",
    image: "/p1.png",
    subtitle: "The Complete Petrosains Experience",
    paragraphs: [
      "Enjoy full access to the Science Centre plus the Energy Capsule Ride—all in one value-packed ticket.",
    ],
  },
  {
    id: "evening-xperience",
    title: "Science Centre Visit",
    image: "/p2.png",
    subtitle: "Where Science Comes Alive",
    paragraphs: [
      "Explore interactive exhibits, hands-on activities, live demonstrations, and exciting STEM experiences for all ages.",
    ],
  },
  {
    id: "priority-access",
    title: "Energy Capsule Ride",
    image: "/p3.png",
    subtitle: "A Journey into Tomorrow",
    paragraphs: [
      "Step into an immersive multimedia ride and discover the future of energy, innovation, and sustainable living in just 11 minutes.",
    ],
  },
  {
    id: "hop-on-hop-off",
    title: "(Combo) Science Centre + Storibox Photo",
    image: "/p4.png",
    subtitle: "Discover. Capture. Take Home the Memory.",
    paragraphs: [
      "Enjoy your Science Centre visit and receive a personalised photo keepsake to remember your experience.",
    ],
  },
];

function AttractionCard({ attraction, gridView, onBuy, transitioning }) {
  return (
    <article
      className={`flex min-w-0 flex-col gap-6 rounded-2xl bg-white p-4 shadow-lg ring-1 ring-black/5 lg:rounded-lg lg:p-3 lg:shadow-sm ${
        gridView ? "lg:gap-4" : "lg:flex-row lg:gap-7"
      }`}
    >
      <div
        className={`w-full shrink-0 ${
          gridView ? "" : "lg:w-2/5"
        }`}
      >
        <img
          src={attraction.image}
          alt={attraction.title}
          loading="lazy"
          decoding="async"
          className="block h-auto w-full rounded-2xl lg:rounded-lg"
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">
        <h2 className="text-lg font-bold leading-6 text-black lg:text-base lg:leading-5">
          {attraction.title}
        </h2>

        <p className="mt-3 flex items-center gap-2 text-xs text-neutral-400 lg:mt-1.5 lg:gap-1 lg:text-sm">
          <FaBolt
            aria-hidden="true"
            className="h-5 w-4 shrink-0 text-amber-300 lg:h-3.5 lg:w-3"
          />
          Instant Confirmation
        </p>

        <div className="mt-7 space-y-5 text-sm leading-relaxed text-black lg:mt-5 lg:space-y-3 lg:leading-snug">
          {attraction.intro && (
            <p className="font-semibold">{attraction.intro}</p>
          )}

          {attraction.notice && (
            <p className="font-semibold text-red-500">
              {attraction.notice}
            </p>
          )}

          {attraction.subtitle && (
            <p className="font-semibold">{attraction.subtitle}</p>
          )}

          {attraction.paragraphs.map((paragraph, index) => (
            <p key={index} className="lg:text-justify">
              {paragraph}

              {attraction.inlineWarning &&
                index === attraction.paragraphs.length - 1 && (
                  <>
                    {" "}
                    <span className="font-semibold text-red-500">
                      {attraction.inlineWarning}
                    </span>
                  </>
                )}
            </p>
          ))}

          {attraction.bullets && (
            <ul className="list-disc pl-12 lg:pl-9">
              {attraction.bullets.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}

          {attraction.afterList && (
            <p className="lg:text-justify">{attraction.afterList}</p>
          )}

          {attraction.warnings && (
            <div className="space-y-2 font-semibold text-red-500 lg:space-y-1">
              {attraction.warnings.map((warning) => (
                <p key={warning}>{warning}</p>
              ))}
            </div>
          )}

          {attraction.linksIntro && <p>{attraction.linksIntro}</p>}

          {attraction.links && (
            <ul className="list-disc pl-12 lg:pl-9">
              {attraction.links.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="break-words hover:underline"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-auto flex justify-end pt-8 lg:pt-12">
          <button
            type="button"
            disabled={transitioning}
            onClick={() => onBuy(attraction)}
            className="inline-flex items-center justify-center rounded-md bg-[#00A78D] px-3 py-2 text-sm font-bold text-white transition-colors duration-200 hover:bg-[#00A78D] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-600 focus-visible:ring-offset-2 disabled:cursor-wait lg:px-3 lg:py-1 lg:text-sm"
          >
            Buy Now
          </button>
        </div>
      </div>
    </article>
  );
}

export default function Hero() {
  const navigate = useNavigate();
  const [gridView, setGridView] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [storageError, setStorageError] = useState("");

  function handleBuy(attraction) {
    if (transitioning) return;

    const selection = {
      ...attraction,
      confirmation: "Instant Confirmation",
    };

    try {
      localStorage.setItem(
        "selectedAttraction",
        JSON.stringify(selection)
      );
    } catch {
      setStorageError("Unable to save your selection. Please try again.");
      return;
    }

    setStorageError("");
    setTransitioning(true);

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    window.setTimeout(
      () => {
        navigate(`/date?attraction=${encodeURIComponent(attraction.id)}`, {
          state: { selectedAttraction: selection },
        });

        window.scrollTo({ top: 0, behavior: "instant" });
      },
      reducedMotion ? 0 : 300
    );
  }

  return (
    <main
      aria-busy={transitioning}
      className={`w-full bg-white font-sans transition-opacity duration-300 motion-reduce:transition-none ${
        transitioning ? "opacity-0" : "opacity-100"
      }`}
    >
      <section aria-labelledby="attractions-title">
        <img
          src="/banner.png"
          alt="Experiences at Petrosains"
          fetchPriority="high"
          className="block h-64 w-full object-cover object-center lg:h-auto"
        />

        <div className="flex flex-col items-center px-4 pb-6 pt-16 lg:px-2.5 lg:pb-4 lg:pt-12">
          <h1
            id="attractions-title"
            className="text-center text-3xl font-extrabold leading-tight text-black"
          >
            Attractions
          </h1>

          <div
            aria-hidden="true"
            className="mt-3 h-2.5 w-24 rounded-full bg-[#00A78D] lg:h-1.5 lg:w-16"
          />
        </div>

        <div className="mx-auto max-w-screen-2xl px-4 pb-16 lg:px-40 lg:pb-12 xl:px-48">
          <div
            role="group"
            aria-label="Display attractions"
            className="mb-6 hidden justify-end gap-2 lg:mb-4 lg:flex lg:gap-1"
          >
            <button
              type="button"
              aria-label="List view"
              aria-pressed={!gridView}
              onClick={() => setGridView(false)}
              className={`rounded transition-colors ${
                !gridView
                  ? "text-[#00A78D]"
                  : "text-neutral-400 hover:text-[#00A78D]"
              }`}
            >
              <FaThList className="h-4 w-4" aria-hidden="true" />
            </button>

            <button
              type="button"
              aria-label="Grid view"
              aria-pressed={gridView}
              onClick={() => setGridView(true)}
              className={`rounded transition-colors ${
                gridView
                  ? "text-[#00A78D]"
                  : "text-neutral-400 hover:text-[#00A78D]"
              }`}
            >
              <FaThLarge className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>

          {storageError && (
            <p
              role="alert"
              className="mb-6 text-xs text-red-600 lg:mb-4"
            >
              {storageError}
            </p>
          )}

          <div
            className={`grid grid-cols-1 gap-8 lg:gap-6 ${
              gridView ? "lg:grid-cols-2" : ""
            }`}
          >
            {ATTRACTIONS.map((attraction) => (
              <AttractionCard
                key={attraction.id}
                attraction={attraction}
                gridView={gridView}
                onBuy={handleBuy}
                transitioning={transitioning}
              />
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}